import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled, { css } from 'styled-components';
import { getAllAdminHeatersService } from '../../../../services';
import {
  getSSRsRatingListService,
  getSSRsSwitchSizesService,
  getZonesInfoForSystemIdentificationService,
} from '../../../../services/systemIdentification.service';
import {
  handleLocationsSystemIdentification,
  handleSSRRating,
  handleSwitchSize,
  selectSysIdentification,
} from '../../../store/slices/settings/admin/sysIdentificationSlice';
import {
  handleAddNewElements,
  selectDescription,
} from '../../../store/slices/ssrDescriptionSlice';
import {
  alignItemsFlexStart,
  flexBoxCenter,
  justifyContentFlexEnd,
  justifyContentFlexStart,
  justifyContentSpaceAround,
  justifyContentSpaceBetween,
  justifyContentSpaceEvenly,
  layerA,
  layerA180Deg,
  layerADark,
  layerB,
  layerCLighter,
} from '../../../styles/commonStyles';
import SystemIdentificationChooser from './SystemIdentificationChooser';
import SelectBox from './SelectBox';
import SSRElements from './SSRElements';
import SettingClearOkMessage from '../../messageBoxes/SettingClearOkMessage';
import FakeSelectBox from './FakeSelectBox';
import AutoCompletePlaces from './AutoCompletePlaces';
import { useRef } from 'react';
import DeleteSwitchMessageBox from '../../messageBoxes/DeleteSwitchMessageBox';
import ErrorMessageBox from '../../messageBoxes/ErrorMessageBox';
import SettingConfirmCancelMessageBox from '../../messageBoxes/SettingConfirmCancelMessageBox';
import ConfirmedUOSInfo from './ConfirmedUOSInfo';
import InitialUOSInfo from './InitialUOSInfo';
import {
  applyHeatingSystemSSRRules,
  areSwitchRowCountsUsable,
  collectConfirmProblems,
  derivePanelCounts,
  formatUOSNumber,
  isSysIdFormDirty,
  makeBlankSwitch,
  reassignSwitchUOS,
  resolvePanelType,
  resizeSwitches,
  shouldDeleteLocation,
  snapshotSysIdForm,
  SYS_ID_MODES,
} from '../../../../helpers/setting';

const SYS_ID_SECTION = 3;

const SystemIdentification = ({
  isCreateEditOrSave,
  sysIdMode,
  setSysIdMode,
  locationNameRef,
  civicAddress,
  setCivicAddress,
  specificLocation,
  setSpecificLocation,
  switches,
  setSwitches,
  handleConfirmOrSave,
  selectedLocation,
  setSelectedLocation,
  numSwitches,
  setNumSwitches,
  numOfUOS,
  setNumOfUOS,
  numOfSSR,
  setNumOfSSR,
  UOSName,
  setUOSName,
  UOSDelete,
  setUOSDelete,
  sysIdResyncToken,
  isGroupedSwitchesSaved,
  setIsGroupedSwitchesSaved,
  isConfirmed,
  setIsConfirmed,
}) => {
  const buttonTitles = ['cancel', 'confirm', 'save'];

  const [listOfUOS, setListOfOUS] = useState([]);
  const [listOfSelectedSSR, setListOfSelectedSSR] = useState([]);
  const [initialStart, setInitialStart] = useState(false);
  const [expandHeatLoad, setExpandHeatLoad] = useState([]);
  const [temporaryGreenCircle, setTemporaryGreenCircle] = useState(false);
  const [openMessageBox, setOpenMessageBox] = useState(false);
  const [saveGasTypeTemporary, setSaveGasTypeTemporary] = useState({});
  const [saveSSRRatingTemporary, setSaveSSRRatingTemporary] = useState({});
  const [saveHeatingSysTemporary, setSaveHeatingSysTemporary] = useState({});
  const [saveSSRSelectedTemporary, setSaveSSRSelectedTemporary] = useState({});
  const [shortCutOfLocationName, setShortCutOfLocationName] = useState('');
  const [deleteSwitch, setDeleteSwitch] = useState(false);
  const [incorrectLocationNameFormat, setIncorrectLocationNameFormat] =
    useState(false);
  const [confirmProblems, setConfirmProblems] = useState([]);
  const [selectSystem, setSelectSystem] = useState(false);

  const civicAddressRef = useRef();

  const formSnapshotRef = useRef(null);

  const dispatch = useDispatch();
  const SysIdentificationState = useSelector(selectSysIdentification);
  const {
    locations,
    heatingSysOptions,
    switchSizeOptions,
    ssrRatingOptions,
    gasTypeOptions,
  } = SysIdentificationState;
  const { elementsOptions } = useSelector(selectDescription);

  const handleCreateUOSAndSSRList = useCallback(
    (UOS, SSR) => {
      const createNumOfUOSArr = [];
      const createNumOfSSRArr = [];
      for (let i = 1; i <= UOS; i++) {
        createNumOfUOSArr.push(formatUOSNumber(i));
      }
      for (let i = 1; i <= SSR; i++) {
        createNumOfSSRArr.push('ssr' + i);
      }
      setListOfOUS(createNumOfUOSArr);
      setListOfSelectedSSR(createNumOfSSRArr);
    },
    [numOfUOS, numOfSSR],
  );

  useEffect(() => {
    const copySwitches = [...switches];
    let sysHeating = {};
    let sysSSRRating = {};
    let sysSSRSelected = {};
    copySwitches.forEach((switches) => {
      sysHeating[switches.UOS] = Array.isArray(sysHeating[switches.UOS])
        ? [...sysHeating[switches.UOS], switches.heatingSys]
        : [switches.heatingSys];
      sysSSRRating[switches.UOS] = Array.isArray(sysSSRRating[switches.UOS])
        ? [...sysSSRRating[switches.UOS], switches.ssrRating]
        : [switches.ssrRating];
      sysSSRSelected[switches.UOS] = Array.isArray(sysSSRSelected[switches.UOS])
        ? [...sysSSRSelected[switches.UOS], switches.selectedSSR]
        : [switches.selectedSSR];
    });

    setSaveHeatingSysTemporary(sysHeating);
    setSaveSSRRatingTemporary(sysSSRRating);
    setSaveSSRSelectedTemporary(sysSSRSelected);
  }, [numSwitches]);

  useEffect(() => {
    getAllAdminHeatersService().then((res) => {
      if (res) {
        dispatch(handleAddNewElements(res));
      }
    });
    getSSRsSwitchSizesService().then((res) => {
      dispatch(handleSwitchSize(res));
    });
    getSSRsRatingListService().then((res) => {
      dispatch(handleSSRRating(res));
    });
    return () => {
      setSaveGasTypeTemporary({});
      setSaveSSRRatingTemporary({});
      setSaveHeatingSysTemporary({});
      setSaveSSRSelectedTemporary({});
    };
  }, []);

  useEffect(() => {
    getZonesInfoForSystemIdentificationService({
      structured: true,
    }).then((res) => {
      dispatch(
        handleLocationsSystemIdentification({
          data: res,
          heaterSpecs: elementsOptions,
        }),
      );
    });
  }, [elementsOptions]);

  useEffect(() => {
    setInitialStart(true);
    setSwitches([makeBlankSwitch()]);
    return () => {
      setSysIdMode(null);
      setIsConfirmed(false);
    };
  }, []);

  const populateFromLocation = (selection) => {
    const location = locations[selection.locationIdx];

    const record =
      selection?.specificLocation &&
      Object.keys(location?.specificLocation ?? {}).length > 0
        ? location.specificLocation[selection.specificLocation]
        : location;

    setCivicAddress(location.address ?? '');
    const ownSpecificLocation =
      typeof location.specificLocation === 'string'
        ? location.specificLocation
        : '';
    setSpecificLocation(selection.specificLocation || ownSpecificLocation);

    setShortCutOfLocationName((selection.location ?? '').split('-')[2] ?? '');

    const copyLocations = JSON.parse(
      JSON.stringify(record?.switchInfo ?? []),
    );
    setSwitches(copyLocations);

    const { numOfUOS: uosCount, numOfSSR: ssrCount } = derivePanelCounts(
      copyLocations,
      record?.numOfUOS,
      record?.numOfSSR,
    );
    setNumOfUOS(uosCount);
    setNumOfSSR(ssrCount);
    handleCreateUOSAndSSRList(uosCount, ssrCount);

    setNumSwitches(copyLocations.length);
  };

  useEffect(() => {
    if (sysIdMode !== SYS_ID_MODES.EDIT || !selectedLocation) return;
    if (!locations[selectedLocation.locationIdx]) return;

    populateFromLocation(selectedLocation);
  }, [sysIdResyncToken]);

  const handleEnterAdd = () => {
    setSwitches([makeBlankSwitch()]);
    setNumOfUOS('');
    setNumOfSSR('');
    setNumSwitches('');
    setCivicAddress('');
    setSpecificLocation('');
    setSysIdMode(SYS_ID_MODES.ADD);
    setIsConfirmed(false);
    setSelectedLocation(null);
    resetConfirmState();
  };

  const handleEnterEdit = (selection) => {
    setSysIdMode(SYS_ID_MODES.EDIT);
    setIsConfirmed(false);
    setSelectedLocation(selection);
    populateFromLocation(selection);
    resetConfirmState();
  };

  const resetConfirmState = () => {
    setIsGroupedSwitchesSaved([]);
    setUOSName([]);
    setExpandHeatLoad([]);
  };

  const readForm = () => ({
    locationName: locationNameRef.current?.value ?? '',
    specificLocation,
    civicAddress,
    numOfUOS,
    numOfSSR,
    numSwitches,
    switches,
  });

  const refreshSnapshot = () => {
    formSnapshotRef.current = snapshotSysIdForm(readForm());
  };

  useEffect(() => {
    if (sysIdMode === null) return;
    refreshSnapshot();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sysIdMode]);

  useEffect(() => {
    if (
      areSwitchRowCountsUsable({ numOfUOS, numOfSSR, numSwitches })
    ) {
      handleNumOfSwitches();
    }
  }, [numOfSSR, numOfUOS, numSwitches]);

  const handleNumOfSwitches = useCallback(() => {
    if (isCreateEditOrSave === 0) {
      if (numSwitches === 0) {
        setSwitches([]);
      } else if (
        areSwitchRowCountsUsable({ numOfUOS, numOfSSR, numSwitches })
      ) {
        handleCreateUOSAndSSRList(numOfUOS, numOfSSR);
        setSwitches(resizeSwitches(switches, numSwitches, makeBlankSwitch));
      } else {
        throw new Error('Please enter # of UOS Panels and SSR QTY.');
      }
    }
    if (isCreateEditOrSave === 1) {
      let copySwitches = [];
      if (switches.length === 0) {
        if (selectedLocation?.specificLocation) {
          const switchInfoArr =
            locations[selectedLocation.locationIdx].specificLocation[
              selectedLocation.specificLocation
            ].switchInfo;
          if (switchInfoArr.length === 0) {
            copySwitches.push(makeBlankSwitch());
          } else {
            copySwitches = JSON.parse(JSON.stringify(switchInfoArr));
          }
        } else {
          if (
            locations[selectedLocation.locationIdx].switchInfo?.length === 0
          ) {
            copySwitches.push(makeBlankSwitch());
          } else {
            copySwitches = JSON.parse(
              JSON.stringify(
                locations[selectedLocation.locationIdx].switchInfo,
              ),
            );
          }
        }

        setSwitches(copySwitches);
      } else {
        setSwitches(resizeSwitches(switches, numSwitches, makeBlankSwitch));

        handleCreateUOSAndSSRList(numOfUOS, numOfSSR);
      }
    }
  }, [numOfSSR, numOfUOS, numSwitches]);

  const handleOpenSelectBoxes = (switchIdx, displaySelectBoxIdx) => {
    const copySwitches = [...switches];
    copySwitches[switchIdx].displaySelectBox[displaySelectBoxIdx] =
      !copySwitches[switchIdx].displaySelectBox[displaySelectBoxIdx];
    setSwitches(copySwitches);
  };

  const handleSelectBoxes = (switchIdx, selection, ObjKey, SelectedUOS) => {
    let copySwitches = [...switches];

    if (ObjKey === 'UOS') {
      copySwitches = reassignSwitchUOS(switches, switchIdx, selection);
    } else if (ObjKey === 'selectedSSR') {
      const allSelectedSSR = selection.map((ssr) => {
        return {
          [ssr]: {
            thermoCouple: 'tc-01',
            isSettingOpen: false,
            specs: [],
          },
        };
      });

      let obj = Object.assign({}, saveSSRSelectedTemporary, {
        [SelectedUOS]: allSelectedSSR,
      });
      setSaveSSRSelectedTemporary(obj);
      copySwitches[switchIdx][ObjKey] = allSelectedSSR;
    } else if (
      ObjKey === 'heatingSys' &&
      (selection === 'ess' || selection === 'tes')
    ) {
      copySwitches[switchIdx][ObjKey] = selection;
      copySwitches[switchIdx].gasType = '';
      if (selection === 'ess') {
        let newObj = Object.assign({}, saveHeatingSysTemporary, {
          [SelectedUOS]: ['ess'],
        });
        setSaveHeatingSysTemporary(newObj);
      } else {
        let newObj = Object.assign({}, saveHeatingSysTemporary, {
          [SelectedUOS]: ['tgs', 'tes'],
        });
        setSaveHeatingSysTemporary(newObj);
      }
    } else if (
      ObjKey === 'heatingSys' &&
      (selection === 'tgs' || selection === 'tgs/tes')
    ) {
      copySwitches[switchIdx] = applyHeatingSystemSSRRules(
        { ...copySwitches[switchIdx], [ObjKey]: selection },
        selection,
      );
      switches.forEach((switchEl, switchElIdx) => {
        if (
          switchEl.UOS === copySwitches[switchIdx].UOS &&
          !copySwitches[switchIdx].gasType
        ) {
          copySwitches[switchElIdx].gasType = saveGasTypeTemporary[SelectedUOS];
        }
      });

      let newObj = Object.assign({}, saveHeatingSysTemporary, {
        [SelectedUOS]: ['tgs', 'tes'],
      });
      setSaveHeatingSysTemporary(newObj);
    } else if (ObjKey === 'ssrRating') {
      let newObj = Object.assign({}, saveSSRRatingTemporary, {
        [SelectedUOS]: [selection],
      });
      setSaveSSRRatingTemporary(newObj);
      copySwitches[switchIdx][ObjKey] = selection;
    } else if (ObjKey === 'gasType') {
      copySwitches.forEach((switchEl) => {
        if (
          switchEl.UOS === SelectedUOS &&
          switchEl.heatingSys !== 'ess' &&
          switchEl.heatingSys !== 'tes'
        ) {
          switchEl.gasType = selection;
        }
      });
    } else {
      copySwitches[switchIdx][ObjKey] = selection;
    }

    setSwitches(copySwitches);
  };

  const handleInputBoxes = (switchIdx, switchKeys, value, trackUOS) => {
    const copySwitches = [...switches];
    copySwitches[switchIdx][switchKeys] = value;
    setSwitches(copySwitches);
    if (trackUOS) {
      let newObj = Object.assign({}, saveGasTypeTemporary, {
        [trackUOS]: value,
      });
      setSaveGasTypeTemporary(newObj);
    }
  };

  const handleDeleteSwitch = (switchIdx) => {
    const {
      UOS,
      type,
      deviceMac,
      heatingSys,
      zoneId,
      latitude,
      longitude,
    } = switches[switchIdx];
    setUOSDelete((prev) => [
      ...prev,
      {
        UOS,
        type: resolvePanelType(type, heatingSys),
        device_mac: deviceMac,
        zone_id: zoneId,
        latitude,
        longitude,
      },
    ]);
    const copySwitches = [...switches];
    copySwitches.splice(switchIdx, 1);
    setNumSwitches(copySwitches.length);
    setSwitches(copySwitches);
    setDeleteSwitch([false]);
    return;
  };

  const handleDeleteSwitchMessageBox = (propArr) => {
    if (propArr[0]) {
      handleDeleteSwitch(propArr[1]);
    } else setDeleteSwitch([false]);
  };

  const [openConfirmMessageBox, setOpenConfirmMessageBox] = useState(false);
  const confirmButtonMessageBoxHandler = () => {
    const form = readForm();
    const problems = collectConfirmProblems(form);

    if (problems.length === 0) {
      const sortedUOSArr = [];
      const UOSNameArr = [];
      const expandHeatLoadArr = [];

      new Array(+numOfUOS).fill(0).forEach((emptyArr, idx) => {
        const numUOS = idx + 1;
        const numStr = formatUOSNumber(numUOS);
        const filteredResult = switches.filter((el) => el.UOS === numStr);

        sortedUOSArr.push(filteredResult);
        expandHeatLoadArr.push(false);

        const displayEl = filteredResult.reduce(
          (
            acc,
            { switchName, switchSize, application, heatingSys, gasType },
          ) => {
            if (heatingSys === 'tgs/tes') {
              const elSplit = heatingSys.split('/');

              return (
                `${acc}` +
                `/ ${switchName} ${switchSize} ${application}:${gasType}`
              );
            } else if (heatingSys === 'tgs') {
              return (
                `${acc}` +
                `/ ${switchName} ${switchSize} ${application}:${gasType}`
              );
            } else {
              return `${acc}` + `/ ${switchName} ${switchSize} ${application}`;
            }
          },
          [],
        );

        UOSNameArr.push(displayEl);
      });

      setIsConfirmed(true);
      setUOSName(UOSNameArr);
      setSwitches(sortedUOSArr);
      setExpandHeatLoad(expandHeatLoadArr);
      setIsGroupedSwitchesSaved(expandHeatLoadArr);
      setTemporaryGreenCircle('confirm');
      setShortCutOfLocationName(form.locationName.split('-')[2] ?? '');
      setOpenConfirmMessageBox(false);
    } else {
      setOpenConfirmMessageBox(false);
      setConfirmProblems(problems);
      setIncorrectLocationNameFormat(true);
    }
  };

  const handleCancel = () => {
    if (isSysIdFormDirty(formSnapshotRef.current, readForm())) {
      setOpenMessageBox(true);
      return;
    }
    exitToChooser();
  };

  const exitToChooser = () => {
    formSnapshotRef.current = null;
    setOpenMessageBox(false);
    setSysIdMode(null);
    setIsConfirmed(false);
    setSelectedLocation(null);
    setCivicAddress('');
    setSpecificLocation('');
    setNumOfUOS('');
    setNumOfSSR('');
    setNumSwitches('');
    setSwitches([]);
    resetConfirmState();
  };

  const handleButtons = (index) => {
    if (index === 0) {
      handleCancel();
      return;
    }

    if (index === 1) {
      setOpenConfirmMessageBox(true);
      return;
    }

    if (index !== 2) return;

    if (shouldDeleteLocation(switches, sysIdMode)) {
      handleConfirmOrSave(SYS_ID_SECTION, 'deleteLocation');
      setTemporaryGreenCircle('save');
      return;
    }

    if (!isConfirmed) {
      const problems = collectConfirmProblems(readForm());
      problems.push('confirm system identification before saving');
      setConfirmProblems(problems);
      setIncorrectLocationNameFormat(true);
      return;
    }

    handleConfirmOrSave(SYS_ID_SECTION);
    if (isGroupedSwitchesSaved.length > 0) {
      handleConfirmOrSave(SYS_ID_SECTION, 'allTheUOSSaved');
      setIsGroupedSwitchesSaved(isGroupedSwitchesSaved.map(() => true));
      setTemporaryGreenCircle('save');
    }
  };

  useEffect(() => {
    const handleTemporaryGreenCircle = setTimeout(() => {
      setTemporaryGreenCircle(false);
    }, 500);

    return () => clearTimeout(handleTemporaryGreenCircle);
  }, [temporaryGreenCircle]);

  const handleExpandHeatLoadConfiguration = (groupSwitchesIdx) => {
    const copyExpandHeatLoad = [...expandHeatLoad];
    copyExpandHeatLoad[groupSwitchesIdx] =
      !copyExpandHeatLoad[groupSwitchesIdx];
    setExpandHeatLoad(copyExpandHeatLoad);
  };

  const handleSaveButtonOfEachUOS = (groupedIdx) => {
    const copyIsGroupedSwitchesSaved = [...isGroupedSwitchesSaved];
    copyIsGroupedSwitchesSaved[groupedIdx] = true;
    setIsGroupedSwitchesSaved(copyIsGroupedSwitchesSaved);
    handleExpandHeatLoadConfiguration(groupedIdx);
  };

  return (
    <BaseLayer>
      <Wrapper>
        <TitleWrapper>
          <Title>
            system identification{sysIdMode ? ` — ${sysIdMode}` : ''}
          </Title>
        </TitleWrapper>
        <ContentBaseLayer>
          {sysIdMode === null ? (
            <SystemIdentificationChooser
              locations={locations}
              onAdd={handleEnterAdd}
              onEdit={handleEnterEdit}
            />
          ) : (
            <ContentWrapper>
              <FlexSpaceEvenlyWrapper>
                <SystemSelectSpacer />
                <IndivFlex location={true}>
                  <SubTitle>location name</SubTitle>
                  <ContentInput
                    type='text'
                    location={true}
                    readOnly={isConfirmed}
                    placeholder='COMPANY-Location Name-SHORTCUT'
                    title='format: COMPANY-Location Name-SHORTCUT (two dashes required)'
                    defaultValue={selectedLocation?.location ?? ''}
                    ref={locationNameRef}
                  ></ContentInput>
                </IndivFlex>
                <IndivFlex specificLocation={true}>
                  <SubTitle>specific location name</SubTitle>
                  <ContentInput
                    required
                    min='3'
                    type='text'
                    specificLocation={true}
                    name='specific location'
                    value={specificLocation}
                    readOnly={isConfirmed}
                    onChange={(e) => setSpecificLocation(e.target.value)}
                  ></ContentInput>
                </IndivFlex>
              </FlexSpaceEvenlyWrapper>
              <FlexStartWrapper>
                <Flex>
                  <IndivFlex isUOS={true}>
                    <SubTitle>number of uos panels</SubTitle>
                    <ContentInput
                      type='number'
                      min='1'
                      step='1'
                      isUOS={true}
                      readOnly={isConfirmed}
                      value={numOfUOS}
                      onChange={(e) => setNumOfUOS(Number(e.target.value))}
                    ></ContentInput>
                  </IndivFlex>
                  <IndivFlex isSSR={true}>
                    <SubTitle>ssr qty.</SubTitle>
                    <ContentInput
                      type='number'
                      min='0'
                      step='1'
                      isSSR={true}
                      readOnly={isConfirmed}
                      value={numOfSSR}
                      onChange={(e) => setNumOfSSR(Number(e.target.value))}
                    ></ContentInput>
                  </IndivFlex>
                  <IndivFlex switches={true}>
                    <SubTitle switches={true}>
                      {selectSystem ? 'number of zones' : 'number of switches'}
                    </SubTitle>
                    <ContentInput
                      type='number'
                      min='1'
                      step='1'
                      switches={true}
                      readOnly={isConfirmed}
                      value={numSwitches}
                      onChange={(e) => setNumSwitches(+e.target.value)}
                    ></ContentInput>
                  </IndivFlex>
                </Flex>
                <Flex isCivilAddress={true}>
                  <IndivFlex address={true}>
                    <SubTitle>civic address</SubTitle>
                    <ContentInput
                      type='text'
                      address={true}
                      readOnly={isConfirmed}
                      value={civicAddress}
                      onChange={(e) => setCivicAddress(e.target.value)}
                      ref={civicAddressRef}
                    ></ContentInput>
                    <AutoCompletePlaces
                      civicAddress={civicAddress}
                      setCivicAddress={setCivicAddress}
                      inputRef={civicAddressRef}
                    />
                  </IndivFlex>
                </Flex>
              </FlexStartWrapper>
              <FlexSwitches>
                <SwitchTitlesWrapper>
                  <SubTitle isUOS={true}>uos</SubTitle>
                  {selectSystem ? (
                    <>
                      <SubTitle isZoneName={true}>zone name</SubTitle>
                      <SubTitle isSegment={true}>segment</SubTitle>
                    </>
                  ) : (
                    <>
                      <SubTitle isSwitchName={true}>switch name</SubTitle>
                      <SubTitle isSwitchSize={true}>switch size</SubTitle>
                    </>
                  )}
                  <SubTitle isSysId={true}> system i.d.</SubTitle>
                  <SubTitle isHeatingSys={true}>heating system</SubTitle>
                  <SubTitle isGasType={true}>gas type</SubTitle>
                  <SubTitle isSelectedSSR={true}>ssr qty.</SubTitle>
                  <SubTitle isSsrRating={true}>ssr rating</SubTitle>
                </SwitchTitlesWrapper>

                {isConfirmed ? (
                  <>
                    <ConfirmedUOSInfo
                      selectSystem={selectSystem}
                      data={switches}
                      switchSizeOptions={switchSizeOptions}
                      isCreateEditOrSave={isCreateEditOrSave}
                      saveHeatingSysTemporary={saveHeatingSysTemporary}
                      heatingSysOptions={heatingSysOptions}
                      listOfSelectedSSR={listOfSelectedSSR}
                      saveSSRRatingTemporary={saveSSRRatingTemporary}
                      ssrRatingOptions={ssrRatingOptions}
                      expandHeatLoad={expandHeatLoad}
                      setSwitches={setSwitches}
                      switches={switches}
                      shortCutOfLocationName={shortCutOfLocationName}
                      UOSName={UOSName}
                      handleSaveButtonOfEachUOS={handleSaveButtonOfEachUOS}
                      handleExpandHeatLoadConfiguration={
                        handleExpandHeatLoadConfiguration
                      }
                    />
                  </>
                ) : (
                  <>
                    {initialStart && (
                      <InitialUOSInfo
                        selectSystem={selectSystem}
                        data={switches}
                        switchSizeOptions={switchSizeOptions}
                        isCreateEditOrSave={isCreateEditOrSave}
                        saveHeatingSysTemporary={saveHeatingSysTemporary}
                        saveSSRSelectedTemporary={saveSSRSelectedTemporary}
                        heatingSysOptions={heatingSysOptions}
                        listOfSelectedSSR={listOfSelectedSSR}
                        handleOpenSelectBoxes={handleOpenSelectBoxes}
                        handleSelectBoxes={handleSelectBoxes}
                        listOfUOS={listOfUOS}
                        ssrRatingOptions={ssrRatingOptions}
                        handleInputBoxes={handleInputBoxes}
                        gasTypeOptions={gasTypeOptions}
                        setDeleteSwitch={setDeleteSwitch}
                        deleteSwitch={deleteSwitch}
                      />
                    )}
                  </>
                )}
              </FlexSwitches>
            </ContentWrapper>
          )}
        </ContentBaseLayer>
        {sysIdMode !== null && (
          <GroupButtonsFlexEnd>
            <GroupButtonsBaseLayer1>
              <GroupButtonsBaseLayer2>
                <GroupButtonsWrapper>
                  {buttonTitles.map((title, index) => {
                    return (
                      <Button
                        isSelectedColor={temporaryGreenCircle === title}
                        disabled={title === 'confirm' && isConfirmed}
                        key={index}
                        onClick={() => {
                          handleButtons(index);
                        }}
                      >
                        <ButtonHole>
                          <ButtonTop>
                            <ButtonTitle>{title}</ButtonTitle>
                          </ButtonTop>
                        </ButtonHole>
                      </Button>
                    );
                  })}
                </GroupButtonsWrapper>
              </GroupButtonsBaseLayer2>
            </GroupButtonsBaseLayer1>
          </GroupButtonsFlexEnd>
        )}
        {deleteSwitch[0] && (
          <MessageBoxWrapper>
            <DeleteSwitchMessageBox
              title={'settings/system identification'}
              subtitle={'administration settings'}
              theme={'system identification'}
              messages={[
                `you are about to delete switch ${
                  switches[deleteSwitch[1]]?.switchName
                } & change the configuration`,
              ]}
              onClose={() => handleDeleteSwitchMessageBox([])}
              onDelete={() => handleDeleteSwitchMessageBox(deleteSwitch)}
            />
          </MessageBoxWrapper>
        )}
        {incorrectLocationNameFormat && (
          <MessageBoxWrapper isLocationName={true}>
            <ErrorMessageBox
              message={{
                content: confirmProblems.join('\n'),
                title: 'settings',
                subtitle: 'administration settings',
                theme: 'missing information',
              }}
              handleClose={() => {
                setIncorrectLocationNameFormat(false);
                setConfirmProblems([]);
              }}
            />
          </MessageBoxWrapper>
        )}
        {openConfirmMessageBox && (
          <MessageBoxWrapper isLocationName={true}>
            <SettingConfirmCancelMessageBox
              onCancel={() => setOpenConfirmMessageBox(false)}
              message={{
                title: 'settings',
                subtitle: 'administration settings',
                theme: 'confirmation',
                message: [
                  'are you sure you want to confirm? once it is confirmed, the data above can no longer be modified.',
                ],
              }}
              onConfirm={() => confirmButtonMessageBoxHandler()}
            />
          </MessageBoxWrapper>
        )}
        {openMessageBox && (
          <SettingClearOkMessage
            onClose={() => setOpenMessageBox(false)}
            title={'unsaved changes'}
            message={
              'this system has changes you have not saved. click "clear" to discard them and go back, or "ok" to keep editing.'
            }
            onClear={exitToChooser}
          />
        )}
      </Wrapper>
    </BaseLayer>
  );
};

export default SystemIdentification;

const BaseLayer = styled.div`
  width: 843px;
  height: auto;
  padding-top: 1px;
  padding-bottom: 1px;

  ${layerB}

  border-radius: 9px;
  opacity: 1;
  ${flexBoxCenter}
`;
const Wrapper = styled.div`
  width: 841px;
  height: auto;

  ${layerA180Deg}

  border-radius: 8px;
  opacity: 1;

  ${justifyContentSpaceEvenly}
  flex-direction: column;
  position: relative;
`;

const TitleWrapper = styled.div`
  width: 829px;
  height: 32px;
  margin-top: 6px;

  ${layerA}

  border-radius: 16px;
  opacity: 1;
  ${flexBoxCenter}
`;

const Title = styled.div`
  color: #ffffff;

  ${({ isHeatLoad }) =>
    isHeatLoad
      ? css`
          margin-top: 8px;
          font-size: 10px;

          display: flex;
          flex-direction: row;

          &:before {
            content: '';
            width: 314px;
            border-bottom: 1px solid #ffffff;
            margin: auto;
            margin-right: 4px;
          }
          &:after {
            content: '';
            width: 314px;
            border-bottom: 1px solid #ffffff;
            margin: auto;
            margin-left: 4px;
          }
        `
      : css`
          font-size: 12px;
          letter-spacing: 1.2px;
        `}
`;

const FlexRow = styled.div`
  position: relative;

  ${({ isLastRow }) =>
    isLastRow === 'sorted'
      ? css`
          ${justifyContentSpaceAround}
          margin-top: 6px;
          margin-bottom: 2px;
        `
      : isLastRow
        ? css`
            ${justifyContentFlexStart}
            margin-top: 6px;
          `
        : css`
            ${justifyContentSpaceEvenly}
          `}

  ${({ isFlexColumn, isFlexStart }) =>
    isFlexColumn
      ? css`
          flex-direction: column;
        `
      : isFlexStart &&
        css`
          display: flex;
          justify-content: space-around;
          align-items: flex-start;
        `}
`;

const ContentBaseLayer = styled.div`
  width: 829px;
  height: auto;
  padding-top: 1px;
  padding-bottom: 1px;
  margin-top: 8px;

  ${layerA}

  border-radius: 18px;
  opacity: 1;
  ${flexBoxCenter}
`;

const ContentWrapper = styled.div`
  width: 827px;
  height: auto;
  padding-top: 4px;
  padding-bottom: 4px;

  ${layerA180Deg}

  border-radius: 17px;
  opacity: 1;
`;

const FlexSpaceEvenlyWrapper = styled.div`
  height: 24px;
  margin-top: 4px;
  margin-bottom: 8px;
  width: 100%;
  ${justifyContentSpaceEvenly}
`;

const SystemSelectSpacer = styled.div`
  width: 112px;
  height: 28px;
`;

const FlexStartWrapper = styled.div`
  width: auto;
  height: 24px;
  margin-top: 4px;
  margin-bottom: 8px;
  ${justifyContentFlexStart}
  gap: 96px;
`;

const Flex = styled.div`
  ${({ isCivilAddress }) =>
    isCivilAddress
      ? css`
          width: 402px;
        `
      : css`
          width: 352px;
        `}
  ${justifyContentSpaceAround}
`;

const IndivFlex = styled.div`
  ${({ location, address, switches, isSSR, isUOS, specificLocation }) =>
    location || specificLocation
      ? css`
          width: 342px;
          ${justifyContentSpaceAround};
          ${({ address }) =>
            address &&
            css`
              ${justifyContentSpaceBetween}
            `}
        `
      : address
        ? css`
            width: 402px;
            ${justifyContentSpaceAround};
          `
        : (switches || isSSR || isUOS) &&
          css`
            max-width: 126px;
            margin-left: 7px;
            ${alignItemsFlexStart};
          `}
`;

const SubTitle = styled.span`
  height: fit-content;
  width: auto;
  text-align: left;
  font-size: 10px;
  letter-spacing: 1px;
  color: #ffffff;
  opacity: 1;
  ${({
    isZoneName,
    isSegment,
    isHeatingSys,
    isSwitchName,
    isSwitchSize,
    isSysId,
    isSsrRating,
    isApp,
    isSwitches,
    isUOS,
    isGasType,
    isSelectedSSR,
  }) =>
    isHeatingSys
      ? css`
          width: 56px;
          position: absolute;
          top: 0px;
          left: 438px;
        `
      : isSwitchName
        ? css`
            height: 14px;
            width: auto;
            position: absolute;
            top: 6px;
            left: 80px;
          `
        : isSwitchSize
          ? css`
              height: 22px;
              width: 52px;
              text-align: center;
              position: absolute;
              top: 0px;
              left: 206px;
            `
          : isSysId
            ? css`
                height: 14px;
                width: auto;
                position: absolute;
                top: 6px;
                left: 308px;
              `
            : isSsrRating
              ? css`
                  height: 14px;
                  width: auto;
                  position: absolute;
                  top: 6px;
                  left: 648px;
                `
              : isApp
                ? css`
                    height: 14px;
                    width: auto;
                    position: absolute;
                    top: 6px;
                    left: 710px;
                  `
                : isSwitches
                  ? css`
                      width: auto;
                    `
                  : isUOS
                    ? css`
                        height: 14px;
                        width: 26px;
                        position: absolute;
                        top: 6px;
                        left: 20px;
                      `
                    : isGasType
                      ? css`
                          width: 34px;
                          text-align: center;
                          position: absolute;
                          top: 0px;
                          left: 524px;
                        `
                      : isSelectedSSR
                        ? css`
                            width: 26px;
                            position: absolute;
                            top: 0px;
                            left: 588px;
                          `
                        : isSegment
                          ? css`
                              height: 14px;
                              width: 64px;
                              text-align: center;
                              position: absolute;
                              top: 6px;
                              left: 202px;
                            `
                          : isZoneName &&
                            css`
                              height: 14px;
                              width: auto;
                              position: absolute;
                              top: 6px;
                              left: 86px;
                            `}
`;

const ContentInput = styled.input`
  ${({ location, address, switches, isUOS, isSSR, specificLocation }) =>
    location
      ? css`
          width: 346px;
          height: 24px;
        `
      : address
        ? css`
            width: 260px;
            height: 24px;
          `
        : specificLocation
          ? css`
              width: 254px;
              height: 24px;
            `
          : (isUOS || isSSR || switches) &&
            css`
              width: 34px;
              height: 24px;
              border-radius: 12px;
            `}

  font-size:8px;
  letter-spacing: 0.8px;
  text-transform: uppercase;

  ${layerADark}

  &::placeholder {
    color: #a9a9a9;
    opacity: 1;
  }

  border-radius: 12px;
  opacity: 1;
`;

const GroupButtonsFlexEnd = styled.div`
  width: 99.2%;
  margin-top: 8px;
  margin-bottom: 4px;
  ${justifyContentFlexEnd}
`;

const GroupButtonsBaseLayer1 = styled.div`
  width: 536px;
  height: 35px;

  ${layerA}

  border-radius: 18px;
  opacity: 1;
  ${flexBoxCenter}
`;

const GroupButtonsBaseLayer2 = styled.div`
  width: 534px;
  height: 33px;

  ${layerA180Deg}

  border-radius: 17px;
  opacity: 1;
  ${flexBoxCenter}
`;

const GroupButtonsWrapper = styled.div`
  width: 530px;
  height: 29px;

  ${layerB}

  border-radius: 15px;
  opacity: 1;
  ${justifyContentSpaceBetween}
`;

const Button = styled.button`
  width: 124px;
  height: 27px;

  ${layerA180Deg}

  border-radius: 25px;
  opacity: 1;
  &:last-child {
    margin-right: 1px;
  }
  &:first-child {
    margin-left: 1px;
  }

  ${({ expandButton }) =>
    expandButton &&
    css`
      width: 75px;
      height: 22px;
    `}

  ${({ isSelectedColor }) =>
    isSelectedColor &&
    css`
      border: 1px solid #95ff45;
    `}

  ${flexBoxCenter}
`;

const ButtonHole = styled.div`
  width: 118px;
  height: 21px;

  ${layerB}

  border-radius: 20px;
  opacity: 0.8;

  ${({ expandButton }) =>
    expandButton &&
    css`
      width: 69px;
      height: 16px;
    `}

  ${flexBoxCenter}
`;

const ButtonTop = styled.div`
  width: 116px;
  height: 19px;

  ${layerA180Deg}

  border-radius: 25px;
  opacity: 1;

  ${({ expandButton }) =>
    expandButton &&
    css`
      width: 67px;
      height: 14px;
    `}

  ${flexBoxCenter}
`;

const ButtonTitle = styled.div`
  font-size: 10px;
  letter-spacing: 1px;
  color: #ffffff;
  opacity: 1;
`;

const FlexSwitches = styled.div`
  width: 100%;
  ${flexBoxCenter}
  flex-direction: column;
`;

const SwitchTitlesWrapper = styled.div`
  height: 24px;
  width: 100%;
  ${flexBoxCenter}
  gap:22.5px;
  position: relative;
`;

const SortedUOSGroupWrapper = styled.div`
  width: 814px;
  height: auto;
  padding: 2px;
  margin-bottom: 6px;

  ${layerA180Deg}

  border-radius: 16px;
`;

const SwitchesBaseLayer = styled.div`
  width: 796px;
  height: 26px;
  margin-top: 2px;

  ${layerADark}

  border-radius: 14px;
  opacity: 1;
  ${flexBoxCenter}

  ${({ isGrouped }) =>
    isGrouped &&
    css`
      width: 750px;
      height: 26px;
    `}
`;

const SwitchesWrapper = styled.div`
  width: 794px;
  height: 24px;

  ${layerA180Deg}

  border-radius: 17px;

  ${justifyContentSpaceAround}

  ${({ isGrouped }) =>
    isGrouped &&
    css`
      width: 748px;
      height: 24px;
    `}
`;

const UOSDisplay = styled.div`
  width: 32px;
  height: 18px;
  margin-top: 6px;

  font-size: 10px;

  ${layerCLighter}
  border-radius: 12px;
  ${flexBoxCenter}
`;

const SwitchesInputBox = styled.input`
  height: 17px;
  ${({ switchName, systemId, application, gasType }) =>
    switchName
      ? css`
          width: 136px;
          ${({ isGrouped }) =>
            isGrouped &&
            css`
              margin-left: 2px;
            `}
        `
      : systemId
        ? css`
            width: 148px;
          `
        : application
          ? css`
              width: 86px;
              margin-right: 2px;
            `
          : gasType &&
            css`
              width: 42px;
            `}

  font-size:10px;
  letter-spacing: 1px;
  text-transform: uppercase;

  ${layerA}

  border-radius: 12px;
`;

const XButtonWrapper = styled.div`
  width: 18px;
  height: 18px;
  margin-left: 2px;

  ${layerA}

  border-radius: 9px;
  opacity: 1;
  ${flexBoxCenter}

  ${({ isGreenBorder }) =>
    isGreenBorder &&
    css`
      border: 1px solid #95ff45;
    `}
`;

const XButton = styled.div`
  width: 16px;
  height: 16px;

  ${layerA180Deg}

  border-radius: 9px;
  opacity: 1;
  ${flexBoxCenter}
`;

const XButtonTop = styled.div`
  width: 13px;
  height: 13px;

  ${layerB}

  border-radius: 9px;
  opacity: 1;
  ${flexBoxCenter}
`;

const MessageBoxWrapper = styled.div`
  ${({ isLocationName }) =>
    isLocationName
      ? css`
          position: absolute;
          z-index: 10;
          width: auto;
          height: auto;
        `
      : css`
          position: absolute;
          z-index: 10;
          top: 30%;
          right: 10%;
          width: auto;
          height: auto;
        `}
`;

const SSRElementsWrapper = styled.div``;

const DisplayUOSName = styled.div`
  width: 287px;
  min-height: 29px;

  background: #233a54 0% 0% no-repeat padding-box;
  box-shadow: inset 0px 0px 6px #000000;
  border-radius: 15px;
  ${flexBoxCenter}
`;

const UOSNameP = styled.p`
  width: 93%;
  font-size: 10px;

  letter-spacing: 1px;
`;

const HeatLoadButtonWrapper = styled.div`
  width: 221px;
  height: 29px;

  ${layerB}
  border-radius: 27px;
  ${flexBoxCenter}

  ${({ isApply }) =>
    isApply &&
    css`
      width: 514px;
      height: 29px;
    `}
`;

const HeatLoadButton = styled.button`
  width: 219px;
  height: 27px;

  ${layerA180Deg}
  border-radius: 25px;
  ${flexBoxCenter}

  ${({ isApply }) =>
    isApply &&
    css`
      width: 512px;
      height: 27px;
    `}
`;

const HeatLoadButtonIndent = styled.div`
  width: 213px;
  height: 21px;

  ${layerB}

  border-radius: 20px;
  ${flexBoxCenter}

  ${({ isApply }) =>
    isApply &&
    css`
      width: 506px;
      height: 21px;
    `}
`;

const HeatLoadButtonTop = styled.div`
  width: 211px;
  height: 19px;

  ${layerA180Deg}

  border-radius: 25px;
  ${flexBoxCenter}

  ${({ isApply }) =>
    isApply &&
    css`
      width: 504px;
      height: 19px;
    `}
`;

const HeatLoadSpan = styled.span`
  font-size: 10px;
`;
